/**
 * Feature Flags Configuration
 *
 * Control premium features and other toggles across the application.
 * Set to false to make features available to all users.
 */

export const featureFlags = {
  /**
   * Enable premium gating for practice modes
   * When false: Mixed Challenge and Beat the Clock are available to all users
   * When true: These modes require premium subscription
   */
  PREMIUM_PRACTICE_MODES: false,

  /**
   * Enable premium gating for leaderboard access
   * When false: Leaderboard is available to all users
   * When true: Leaderboard requires premium subscription
   */
  PREMIUM_LEADERBOARD: false,

  /**
   * Enable premium gating for advanced techniques
   * When false: All techniques are available to everyone
   * When true: Some techniques require premium subscription
   */
  PREMIUM_TECHNIQUES: false,

  /**
   * Show PRO badges on premium features
   * When false: No PRO badges are shown
   * When true: PRO badges appear on premium features
   */
  SHOW_PRO_BADGES: false,

  /**
   * Enable Chargebee billing integration
   * When false: All checkout/billing UI is hidden, no premium gating on pricing page
   * When true: Full Chargebee checkout and billing portal enabled
   */
  CHARGEBEE_BILLING: false,

  /**
   * Enable Track 2: The Olympiad Path
   * When false: Olympiad track is hidden
   * When true: Shows Math Olympiad problem-solving techniques
   */
  OLYMPIAD_TRACK: true,
};

/**
 * Helper function to check if a feature requires premium
 */
export function isPremiumRequired(feature: keyof typeof featureFlags): boolean {
  return featureFlags[feature] === true;
}

/**
 * Check if user has premium access (paid subscription OR school member).
 * School members get full access regardless of subscription.
 */
export function userHasPremiumAccess(user: {
  is_premium?: boolean;
  subscription_tier?: string;
  subscription_status?: string;
  school_id?: string;
} | null): boolean {
  if (!user) return false;
  // School members always have access
  if (user.school_id) return true;
  // Check subscription
  const tier = user.subscription_tier || "free";
  const status = user.subscription_status || "none";
  if (tier === "free") return false;
  return status === "active" || status === "cancelled" || status === "non_renewing";
}
